import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { useOpenRecipientCapBlocked, useRecipientCap } from "@/hooks/useRecipientCap";
import LinkQuantityStepper from "./LinkQuantityStepper";
import SenderInfoFields from "./SenderInfoFields";

// Verbatim from the story (warning header + body).
const TERMS_HEADER = "Read before you create a universal link";
const TERMS_BODY =
  "Anyone who has the link can redeem a gift, including the same person more than once, until the redemption limit is reached.";
// Verbatim legal copy from the prototype's .universal-terms-legal (Oct 2026 revisions).
const TERMS_LEGAL = [
  "CUSTOMER EXPRESSLY ACKNOWLEDGES AND ACCEPTS THAT WITH MULTIPLE-USE REDEMPTION LINKS, THERE IS A RISK THAT A SINGLE RECIPIENT COULD REPEATEDLY REDEEM AND DEPLETE THE ENTIRE CAMPAIGN BUDGET. CUSTOMER SHALL BE SOLELY RESPONSIBLE FOR ALL FEES ASSOCIATED WITH ANY AND ALL REDEMPTIONS MADE THROUGH A MULTIPLE-USE REDEMPTION LINK, REGARDLESS OF THE NUMBER OF REDEMPTIONS BY ANY INDIVIDUAL RECIPIENT(S).",
  "Gifts for Good expressly disclaims any and all liability related to multiple redemptions by recipients, and Customer shall not be entitled to a refund or credit for redemptions made through a multiple-use Redemption Link.",
];

const QTY_LABEL = "How many gifts can be redeemed through this link?";
const QTY_TOOLTIP =
  "Gift links don't come with an introduction email, so the unboxing page uses these details to say who the gift is from.";
const LINK_HELPER = "Goes live when your campaign launches, or on your activation date.";
const CAUTION =
  "Anyone with this link can redeem a gift, and a determined person could redeem more than once. Only share it where you're comfortable with that risk.";
const SENDER_HELPER =
  "Gift links don't come with an introduction email, so the unboxing page uses these details to say who the gift is from.";

/**
 * Step-2 panel for the "Generate a single universal link" send method. Before the sender accepts the
 * multiple-use terms it shows the disclaimer + "ACCEPT AND GENERATE LINK" (Continue stays blocked);
 * after acceptance it shows the public link + a persistent caution, the redemption-limit stepper, and
 * the campaign-level sender fields (reused from Manual Distribution).
 */
export default function UniversalLinkPanel() {
  const {
    universalAccepted,
    acceptUniversal,
    universalToken,
    linkQuantity,
    setLinkQuantity,
    senderFirstName,
    setSenderFirstName,
    senderLastName,
    setSenderLastName,
    senderCompany,
    setSenderCompany,
    senderSignature,
    setSenderSignature,
    selectedBudget,
    showToast,
  } = useCampaignBuilder();
  // cap is null (unlimited) until the matrix resolves, so nothing is clamped on an unknown plan.
  const { cap } = useRecipientCap();
  const openCapBlocked = useOpenRecipientCapBlocked();

  const url = universalToken ? `${window.location.origin}/u/${universalToken}` : "";

  function copyUrl() {
    if (!url) return;
    // The URL is also shown in the read-only field above, so a copy failure (permission denied,
    // non-secure origin, no Clipboard API) isn't a dead-end — but tell the user so they can select
    // and copy it by hand rather than think the click did nothing.
    const copyFailed = "Couldn't copy automatically — select the link above and copy it manually.";
    const done = navigator.clipboard?.writeText(url);
    if (!done) {
      showToast(copyFailed);
      return;
    }
    done
      .then(() => showToast("Universal link copied to clipboard"))
      .catch(() => showToast(copyFailed));
  }

  // Before acceptance: the multiple-use disclaimer + the generate button. The link panel below stays
  // hidden (and Step 2's Continue is blocked) until the terms are accepted.
  if (!universalAccepted) {
    return (
      <div className="mt-5 rounded-card border border-smoke bg-white px-4 pt-4 pb-3.5">
        <div className="flex items-start gap-2.5 mb-3">
          <WarningAmberIcon sx={{ fontSize: 22 }} className="text-good-wine shrink-0" />
          <div>
            <p className="font-lato text-[14px] font-bold text-rodeo-black m-0">{TERMS_HEADER}</p>
            <p className="font-lato text-[12.5px] text-graphite leading-normal mt-0.5 mb-0">
              {TERMS_BODY}
            </p>
          </div>
        </div>
        {/* Scrollable legal box (wax background), matching the prototype's .universal-terms-legal.
            The full text is in the DOM (screen readers read all of it); the box just caps the visual
            height and scrolls with mouse/trackpad. */}
        <div className="max-h-33 overflow-y-auto rounded-lg bg-wax px-3 py-2.5 font-lato text-[11.5px] leading-[1.55] text-rodeo-black">
          {TERMS_LEGAL.map((para) => (
            <p key={para.slice(0, 24)} className="m-0 mb-2 last:mb-0">
              {para}
            </p>
          ))}
        </div>
        <button
          type="button"
          onClick={acceptUniversal}
          className="mt-3 px-4 py-2.5 rounded-btn font-lato font-bold text-[12px] tracking-[1px] uppercase bg-rodeo-black text-white hover:opacity-90 transition-opacity cursor-pointer"
        >
          Accept and generate link
        </button>
      </div>
    );
  }

  // After acceptance: the public link, the persistent caution, the redemption-limit stepper + sender.
  return (
    <div className="mt-5">
      <div className="rounded-card border border-smoke bg-white p-4">
        <p className="font-lato text-[14px] font-semibold text-rodeo-black mb-2">
          Your universal link
        </p>
        <div className="flex items-stretch gap-2">
          <input
            readOnly
            value={url || "Generating your link…"}
            aria-label="Universal link"
            className="flex-1 min-w-0 font-lato text-[14px] text-rodeo-black px-3 py-2.5 rounded-lg outline-none bg-white border border-smoke"
          />
          <button
            type="button"
            onClick={copyUrl}
            disabled={!url}
            aria-label="Copy universal link"
            className="shrink-0 flex items-center gap-1.5 px-3.5 rounded-lg font-lato text-[12px] font-bold border border-smoke bg-white text-rodeo-black hover:border-dark-dust hover:bg-clear-dust transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ContentCopyIcon sx={{ fontSize: 16 }} />
            Copy
          </button>
        </div>
        <p className="font-lato text-[12px] text-graphite mt-2 leading-normal">{LINK_HELPER}</p>
      </div>

      {/* Persistent wine-tinted caution, matching the prototype's .universal-caution. */}
      <div className="mt-3 flex items-start gap-2 rounded-lg border border-good-wine-4 bg-good-wine-bg px-3 py-2.5">
        <WarningAmberIcon sx={{ fontSize: 18 }} className="text-good-wine shrink-0 mt-px" />
        <p className="font-lato text-[12.5px] text-rodeo-black leading-normal m-0">
          <strong className="font-bold text-good-wine">Caution:</strong> {CAUTION}
        </p>
      </div>

      <div className="mt-5">
        <LinkQuantityStepper
          label={QTY_LABEL}
          tooltip={QTY_TOOLTIP}
          value={linkQuantity}
          onChange={setLinkQuantity}
          tier={selectedBudget ?? 0}
          cap={cap}
          onOverCap={openCapBlocked}
        />
      </div>
      <SenderInfoFields
        className="mt-5"
        firstName={senderFirstName}
        onFirstName={setSenderFirstName}
        lastName={senderLastName}
        onLastName={setSenderLastName}
        company={senderCompany}
        onCompany={setSenderCompany}
        signature={senderSignature}
        onSignature={setSenderSignature}
        helper={SENDER_HELPER}
      />
    </div>
  );
}
